"use client";

import { useEffect, useRef, useState } from "react";
import "leaflet/dist/leaflet.css";
import type { Map as LeafletMap, Marker } from "leaflet";
import { FIELD_LABEL } from "./ui";

/**
 * Clinic-location pin picker (docs/ux/screens/BusinessDetailsScreen.md #3-#4).
 * Leaflet + OpenStreetMap tiles; click to drop, drag to adjust. The pin's
 * coordinates land in hidden latitude/longitude inputs for the form post.
 * A divIcon pin avoids Leaflet's bundler-hostile image assets.
 */

const LEBANON_CENTER: [number, number] = [33.8938, 35.5018];

const PIN_HTML = `<div style="
  width: 22px; height: 22px; border-radius: 50% 50% 50% 0;
  background: #101720; transform: rotate(-45deg);
  border: 2px solid #ffffff; box-shadow: 0 1px 4px rgba(0,0,0,.4);
  margin: -18px 0 0 -11px;"></div>`;

export function MapPicker({
  error,
  initial,
  label = "Clinic location",
  onChange,
  emitInputs = true,
}: {
  error?: string;
  /** Prefills the pin (BusinessProfileEditScreen: it can be moved, not removed). */
  initial?: { lat: number; lng: number };
  /** Field label, overridden when reused outside the registration form. */
  label?: string;
  /** Fires whenever the pin is placed or dragged (Discovery search reuse). */
  onChange?: (pos: { lat: number; lng: number }) => void;
  /** Emit the hidden latitude/longitude inputs (off when not inside a form). */
  emitInputs?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<LeafletMap | null>(null);
  const markerRef = useRef<Marker | null>(null);
  const [position, setPosition] = useState<{ lat: number; lng: number } | null>(
    initial ?? null,
  );
  const [loadFailed, setLoadFailed] = useState(false);
  const [geoNote, setGeoNote] = useState<string | null>(null);
  const [retryKey, setRetryKey] = useState(0);
  // Stable across renders so the init effect doesn't depend on an object prop.
  const initialRef = useRef(initial);
  // Latest onChange, so the init effect's closures never go stale.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const L = (await import("leaflet")).default;
        if (cancelled || !containerRef.current || mapRef.current) return;

        const map = L.map(containerRef.current).setView(LEBANON_CENTER, 9);
        L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        }).addTo(map);

        const icon = L.divIcon({ className: "", html: PIN_HTML });

        const placePin = (lat: number, lng: number) => {
          if (!markerRef.current) {
            markerRef.current = L.marker([lat, lng], { icon, draggable: true }).addTo(map);
            markerRef.current.on("dragend", () => {
              const p = markerRef.current!.getLatLng();
              setPosition({ lat: p.lat, lng: p.lng });
              onChangeRef.current?.({ lat: p.lat, lng: p.lng });
            });
          } else {
            markerRef.current.setLatLng([lat, lng]);
          }
          setPosition({ lat, lng });
          onChangeRef.current?.({ lat, lng });
        };

        map.on("click", (e) => placePin(e.latlng.lat, e.latlng.lng));
        mapRef.current = map;
        // Expose for the geolocation button below.
        (map as unknown as { __placePin?: typeof placePin }).__placePin = placePin;

        const startAt = initialRef.current;
        if (startAt) {
          map.setView([startAt.lat, startAt.lng], 15);
          placePin(startAt.lat, startAt.lng);
        }
      } catch {
        if (!cancelled) setLoadFailed(true);
      }
    })();

    return () => {
      cancelled = true;
      mapRef.current?.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
  }, [retryKey]);

  function useMyLocation() {
    setGeoNote(null);
    if (!navigator.geolocation) {
      setGeoNote("Couldn't get your location, drop the pin manually");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const map = mapRef.current as
          | (LeafletMap & { __placePin?: (lat: number, lng: number) => void })
          | null;
        map?.setView([coords.latitude, coords.longitude], 15);
        map?.__placePin?.(coords.latitude, coords.longitude);
      },
      () => setGeoNote("Couldn't get your location, drop the pin manually"),
    );
  }

  return (
    <div className="mb-4">
      <span className={FIELD_LABEL}>
        {label}
      </span>
      {loadFailed ? (
        <div className="flex h-64 w-full flex-col items-center justify-center rounded-lg border border-line bg-page text-sm text-muted">
          <p className="mb-2">The map couldn&apos;t load.</p>
          <button
            type="button"
            onClick={() => {
              setLoadFailed(false);
              setRetryKey((k) => k + 1);
            }}
            className="font-medium text-accent"
          >
            Retry
          </button>
        </div>
      ) : (
        <div
          ref={containerRef}
          className="h-64 w-full rounded-lg border border-line"
          aria-label="Map: click to drop a pin on your clinic's location"
        />
      )}
      <div className="mt-2 flex items-center gap-3">
        <button
          type="button"
          onClick={useMyLocation}
          className="text-sm font-medium text-accent"
        >
          Use my location
        </button>
        {position && (
          <span className="text-xs text-muted">
            Pin set ({position.lat.toFixed(4)}, {position.lng.toFixed(4)})
          </span>
        )}
      </div>
      {geoNote && <p className="mt-1 text-sm text-muted">{geoNote}</p>}
      {error && !position && <p className="mt-1 text-sm text-danger">{error}</p>}
      {emitInputs && (
        <>
          <input type="hidden" name="latitude" value={position?.lat ?? ""} />
          <input type="hidden" name="longitude" value={position?.lng ?? ""} />
        </>
      )}
    </div>
  );
}
